import { Box, Collapse } from "@mui/material";
import { type FC, type ReactNode, useId } from "react";
import { MOTION } from "@/theme/tokens";
import { CollapsibleCardSection } from "./CollapsibleCardSection";
import { HeaderContent } from "./CollapsibleSectionHeader";
import { HEADER_SX, isRichSection } from "./collapsibleSectionSx";
import { CollapsibleSectionVariant } from "./collapsibleSectionVariant";

/**
 * What the header opens: its own block underneath (`children`), or regions the caller renders elsewhere (`controls`,
 * their element ids) — a toggle whose folded content sits in the same grid as everything else rather than in one box
 * under it. With `controls` the caller collapses each region itself and gives it the matching id.
 */
type CollapsibleSectionRegion = { children: ReactNode; controls?: never } | { children?: never; controls: readonly string[] };

export type CollapsibleSectionProps = CollapsibleSectionRegion & {
	/** Already-translated section title — the disclosure's accessible name. */
	title: string;
	open: boolean;
	onToggle: () => void;
	// DEFAULT: `Section`, the full-width settings header every existing caller renders.
	variant?: CollapsibleSectionVariant;
	/**
	 * Decorative glyph before the title. `Inline` sets it at caption size; `Card` draws it in a brand disc; `Section` sets
	 * it as given, so pass a sized node (an `AccentIconTile` with `component="span"` — the header is a `<button>`, so
	 * phrasing content only).
	 */
	// DEFAULT: absent = the title alone.
	icon?: ReactNode;
	/**
	 * Already-translated muted line under a `Section` or `Disclosure` title — what opening it is FOR ("Add someone
	 * without a CV" / "A referral, a walk-in…"; "By role, cause, day and person"). Part of the button, so it is read
	 * with the title.
	 */
	// DEFAULT: absent = the title alone. `Section` and `Disclosure` only.
	description?: string;
	/**
	 * Already-translated current value, shown muted after the title (`Eyebrow` only): what the group is set
	 * to, so it can stay collapsed without hiding the answer. Part of the header's accessible name.
	 */
	// DEFAULT: absent = the title alone.
	summary?: string;
	/** Id for the title, so a group inside the region can point its `aria-labelledby` at it. */
	// DEFAULT: absent = the title carries no id; nothing inside the region refers to it.
	titleId?: string;
	/** Trailing text in the header, before the chevron (`Card` only), e.g. "Today · 10:00 AM". */
	// DEFAULT: absent = no meta.
	meta?: string;
	dataTestId?: string;
};

/**
 * A titled disclosure: a header row that expands and collapses the block under it.
 *
 * For stacking a long settings surface into named groups where each group is arbitrary content rather than
 * a fixed control type — `FacetFilterSection` is the checkbox-list flavour of the same idea, and this is the
 * content-agnostic one.
 *
 * The header is a native `<button>` so it is reachable and operable from the keyboard, and carries
 * `aria-expanded`/`aria-controls` so assistive technology can report the state and find the region — its own, or
 * with `controls` the external regions it opens. The chevron is decorative only — it must NOT be a nested `<button>`,
 * which is invalid inside one. The region stays mounted while collapsed, so state inside it (a half-typed field, an
 * open dialog) survives a toggle.
 */
export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
	title,
	open,
	onToggle,
	children,
	controls,
	variant = CollapsibleSectionVariant.Section,
	icon,
	summary,
	titleId,
	meta,
	description,
	dataTestId,
}) => {
	const regionId = useId();

	if (variant === CollapsibleSectionVariant.Card) {
		if (controls !== undefined) throw new Error("CollapsibleSection's Card variant opens its own region; it can't take `controls`");
		return (
			<CollapsibleCardSection
				title={title}
				open={open}
				onToggle={onToggle}
				icon={icon}
				titleId={titleId}
				meta={meta}
				regionId={regionId}
				dataTestId={dataTestId}
			>
				{children}
			</CollapsibleCardSection>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			<Box
				component="button"
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={controls === undefined ? regionId : controls.join(" ")}
				data-testid={dataTestId}
				sx={{
					display: "flex",
					alignItems: "center",
					// The button resets come FIRST so a variant can put a border or surface back on (`Disclosure` does).
					border: "none",
					background: "none",
					font: "inherit",
					cursor: "pointer",
					...(isRichSection(variant, icon, description) ? HEADER_SX.rich : HEADER_SX[variant]),
					transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
				}}
			>
				<HeaderContent variant={variant} title={title} open={open} icon={icon} summary={summary} titleId={titleId} description={description} />
			</Box>
			{controls === undefined && (
				<Collapse in={open} id={regionId}>
					{children}
				</Collapse>
			)}
		</Box>
	);
};
